import { Injectable } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { formatDateToISO } from '../shared/utils/date.util';
import { EmployeeProfileResponse } from '../employee/models/employee-profile.model';

export interface EmployeeData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  defaultOpeningBalance: number;
  employeeId: string;
  role: string;
  location: string;
  startDate: string | Date;
  status: string;
  username: string;
  password: string;
  inviteLink: string;
  profilePhoto?: File;
}

export interface EmployeeApiResponse {
  status: boolean;
  status_code: number;
  message: string;
  data?: any;
}

/**
* Service for managing employee data, profiles, break-hour settings, and reward statistics via the centralized ApiService.
* @component
* @selector app-employee-service
* @example
*   <app-employee-service></app-employee-service>
* @input {none} none - This injectable has no Angular @Input() properties; it is consumed via Dependency Injection.
* @output {none} none - This injectable does not emit Angular @Output() events; it exposes Observables from its methods instead.
*
* Purpose:
* - Acts as a single place to perform CRUD operations for employees, fetch and transform paginated lists, manage employee profiles, configure break-hour settings, and retrieve reward statistics.
*
* Key methods:
* - createEmployee(employeeData: EmployeeData): Observable<any> - Builds a FormData payload (including optional profile photo and ISO-formatted start date) and posts to the 'employee' endpoint to create a new employee.
* - getEmployees(): Observable<any> - Retrieves the raw list of employees via GET 'employee'.
* - loadEmployees(page?: number, limit?: number, searchTerm?: string, filters?: { locations?: string[]; status?: string }): Observable<{employees, totalItems, currentPage, totalPages, itemsPerPage}> - Loads a paginated, searchable and filterable employee list; transforms the API response into a consistent shape for components.
* - getEmployeeById(employeeId: string): Observable<any> - Fetches a single employee by ID.
* - getEmployeeProfile(employeeId: string): Observable<EmployeeProfileResponse> - Retrieves the profile for a specific employee.
* - getCurrentEmployeeProfile(): Observable<any> - Retrieves the currently authenticated employee's profile.
* - updateEmployee(employeeId: string, employeeData: EmployeeData): Observable<any> - Updates an employee by sending a FormData payload via PUT to 'employee/{id}'.
* - deleteEmployee(employeeId: string): Observable<any> - Deletes an employee via DELETE 'employee/{id}'.
* - getBreakHourSettings(): Observable<any> - Retrieves saved break-hour configuration for employees.
* - updateBreakPolicyStatus(status: boolean): Observable<any> - Toggles the enable/disable break policy via PUT 'employee/break-hour'.
* - updateBreakHourFormSettings(settings: { break_duration: number; work_hours: number; notify_before: number; notify_via_email: boolean }): Observable<any> - Posts break-hour form settings to 'employee/break-hour'.
* - getRewardStatistics(startDate: string, endDate: string): Observable<any> - Retrieves reward statistics for the provided date range.
*
* Important internal/helper methods:
* - private appendEmployeeDataToFormData(formData: FormData, employeeData: EmployeeData): void - Normalizes and appends employee fields (first/last name, username, email, phone, role, ISO-formatted start date, location, password, invite link and optional profile photo) to FormData used by create/update methods.
*
* Lifecycle and usage notes:
* - ProvidedIn: 'root' makes this a singleton service available application-wide through Angular DI.
* - All HTTP interactions are delegated to ApiService; ensure ApiService is configured with the proper base URL, headers, and error handling.
* - Methods return RxJS Observables; callers should subscribe, pipe, or use the async pipe and handle errors appropriately.
* - loadEmployees applies defensive defaults and response transformation so UI components can rely on a stable shape even when the API response lacks expected fields.
*/
@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  private currentProfileCache$: Observable<any> | null = null;

  constructor(private apiService: ApiService) { }

  createEmployee(employeeData: EmployeeData): Observable<any> {
    const formData = new FormData();
    this.appendEmployeeDataToFormData(formData, employeeData);
    return this.apiService.post('employee', formData);
  }

  getEmployees(): Observable<any> {
    return this.apiService.get('employee');
  }

  /**
  * Fetches a paginated list of employees from the API, applying optional search and filters.
  * @example
  * oadEmployees(1, 10, 'john', { locations: ['1','2'], status: 'active' })
  * Observable<{ employees: any[]; totalItems: number; currentPage: number; totalPages: number; itemsPerPage: number }>
  * @param {{number}} {{page}} - Page number to retrieve (1-based).
  * @param {{number}} {{limit}} - Number of items per page.
  * @param {{string}} {{searchTerm}} - Optional search term to filter employees.
  * @param {{{ locations?: string[]; status?: string }}} {{filters}} - Optional filters: location ids and employee status.
  * @returns {{Observable<any>}} Observable that emits an object containing employees array and pagination metadata.
  **/
  loadEmployees(
    page = 1,
    limit = 10,
    searchTerm = '',
    filters: { locations?: string[]; status?: string } = {}
  ): Observable<any> {
    // Build query parameters
    const params: any = {
      page: page.toString(),
      limit: limit.toString()
    };

    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

    if (filters.locations && filters.locations.length > 0) {
      params.location_ids = filters.locations.join(',');
    }

    if (filters.status) {
      params.status = filters.status;
    }

    return this.apiService.get('employee', params).pipe(
      map((response: any) => {
        // Transform API response to match component expectations
        if (response.status && response.data) {
          return {
            employees: response.data.data || [],
            totalItems: response.data.pagination?.total || 0,
            currentPage: response.data.pagination?.page || 1,
            totalPages: response.data.pagination?.totalPages || 1,
            itemsPerPage: response.data.pagination?.limit || limit
          };
        }
        return {
          employees: [],
          totalItems: 0,
          currentPage: 1,
          totalPages: 1,
          itemsPerPage: limit
        };
      })
    );
  }

  getEmployeeById(employeeId: string): Observable<any> {
    return this.apiService.get(`employee/${employeeId}`);
  }

  getEmployeeProfile(employeeId: string): Observable<EmployeeProfileResponse> {
    return this.apiService.get(`employee/${employeeId}`);
  }

  getCurrentEmployeeProfile(): Observable<any> {
    if (!this.currentProfileCache$) {
      this.currentProfileCache$ = this.apiService.getCurrentEmployeeProfile().pipe(
        shareReplay(1)
      );
    }
    return this.currentProfileCache$;
  }

  /**
   * Clears the cached employee profile, forcing a fresh fetch on the next request.
   * Useful during logout or when profile data is known to have changed.
   */
  clearProfileCache(): void {
    this.currentProfileCache$ = null;
  }

  updateEmployee(employeeId: string, employeeData: EmployeeData): Observable<any> {
    const formData = new FormData();
    this.appendEmployeeDataToFormData(formData, employeeData);
    return this.apiService.put(`employee/${employeeId}`, formData).pipe(
      tap(() => this.clearProfileCache())
    );
  }

  /**
  * Append employee data fields to the provided FormData object for submission.
  * @example
  * appendEmployeeDataToFormData(formData, employeeData)
  * undefined
  * @param {{FormData}} {{formData}} - The FormData object to append employee fields to.
  * @param {{EmployeeData}} {{employeeData}} - The employee data object containing fields to append (firstName, lastName, email, username, phoneNumber, role, startDate, location, password, inviteLink, profilePhoto).
  * @returns {{void}} No return value; the function mutates the provided FormData.
  **/
  private appendEmployeeDataToFormData(formData: FormData, employeeData: EmployeeData): void {
    const startDate = formatDateToISO(employeeData.startDate);
    formData.append('first_name', employeeData.firstName);
    formData.append('last_name', employeeData.lastName);
    formData.append('email', employeeData.email);
    formData.append('username', employeeData.username);
    formData.append('phone_number', employeeData.phoneNumber);
    formData.append('default_opening_balance', employeeData.defaultOpeningBalance.toString());
    formData.append('designation', employeeData.role);
    formData.append('start_date', startDate);
    formData.append('location_id', employeeData.location);
    formData.append('password', employeeData.password);
    formData.append('link', employeeData.inviteLink);
    if (employeeData.profilePhoto) {
      formData.append('picture_url', employeeData.profilePhoto);
    }
  }

  deleteEmployee(employeeId: string): Observable<any> {
    return this.apiService.delete(`employee/${employeeId}`);
  }

  // Break Hour Settings API methods
  getBreakHourSettings(): Observable<any> {
    // GET request without payload to fetch saved break hour settings
    return this.apiService.get('employee/break-hour');
  }

  // PUT API - Update ONLY the Enable Break Policy toggle status
  updateBreakPolicyStatus(status: boolean): Observable<any> {
    return this.apiService.put('employee/break-hour', { status });
  }

  // POST API - Update form field settings (break_duration, work_hours, notify_before, notify_via_email)
  updateBreakHourFormSettings(settings: {
    break_duration: number;
    work_hours: number;
    notify_before: number;
    notify_via_email: boolean;
  }): Observable<any> {
    return this.apiService.post('employee/break-hour', settings);
  }

  // Reward Statistics API
  getRewardStatistics(startDate: string, endDate: string): Observable<any> {
    const params = {
      start_date: startDate,
      end_date: endDate
    };
    return this.apiService.get('employee/reward-statistics-by-admin', params);
  }
}
